<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const { t } = useI18n();

const showDeleteModal = ref(false);
const selectedBoard = ref({});

const boards = computed(() => getters['kanban/boards/getBoards'].value);
const uiFlags = computed(() => getters['kanban/boards/getUIFlags'].value);

const tableHeaders = computed(() => [
  t('KANBAN.SETTINGS.TABLE.NAME'),
  t('KANBAN.SETTINGS.TABLE.DESCRIPTION'),
  t('KANBAN.SETTINGS.TABLE.STEPS'),
  t('KANBAN.SETTINGS.TABLE.ACTIONS'),
]);

const goToNew = () => {
  router.push({ name: 'settings_kanban_new' });
};

const goToEdit = board => {
  router.push({ name: 'settings_kanban_edit', params: { boardId: board.id } });
};

const openDelete = board => {
  selectedBoard.value = board;
  showDeleteModal.value = true;
};

const closeDelete = () => {
  showDeleteModal.value = false;
};

const confirmDelete = async () => {
  const id = selectedBoard.value.id;
  closeDelete();
  try {
    await store.dispatch('kanban/boards/delete', id);
    useAlert(t('KANBAN.SETTINGS.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(t('KANBAN.SETTINGS.DELETE.ERROR'));
  }
};

onBeforeMount(() => {
  store.dispatch('kanban/boards/fetch');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('KANBAN.STATE.LOADING')"
    :no-records-found="!boards.length"
    :no-records-message="$t('KANBAN.SETTINGS.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('KANBAN.SETTINGS.HEADER')"
        :description="$t('KANBAN.SETTINGS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            :label="$t('KANBAN.SETTINGS.NEW_BOARD')"
            icon="i-lucide-plus"
            size="sm"
            @click="goToNew"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable :headers="tableHeaders" :items="boards">
        <template #row="{ items }">
          <BaseTableRow v-for="board in items" :key="board.id" :item="board">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12">
                  {{ board.name }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ board.description }}
                </span>
              </BaseTableCell>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ (board.steps || []).length }}
                </span>
              </BaseTableCell>
              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-tooltip.top="$t('KANBAN.SETTINGS.ACTIONS.EDIT')"
                    icon="i-lucide-pencil"
                    slate
                    sm
                    @click="goToEdit(board)"
                  />
                  <Button
                    v-tooltip.top="$t('KANBAN.SETTINGS.ACTIONS.DELETE')"
                    icon="i-lucide-trash"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="openDelete(board)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDelete"
      :title="$t('KANBAN.SETTINGS.DELETE.TITLE')"
      :message="$t('KANBAN.SETTINGS.DELETE.MESSAGE')"
      :message-value="` ${selectedBoard.name}?`"
      :confirm-text="$t('KANBAN.SETTINGS.DELETE.YES')"
      :reject-text="$t('KANBAN.SETTINGS.DELETE.NO')"
    />
  </SettingsLayout>
</template>
